import { ThemeEvents } from '@theme/events';

/**
 * Client-side display of saved dress names (localStorage) across the storefront.
 * Keys: be_dress_name:{productId}_{variantId} — payload `{ n, at }` with optional 7-day TTL; cleared when the variant is in the cart.
 */

/** @param {string} pid @param {string} vid */
function readDressName(pid, vid) {
  try {
    const api = window.__beDressNameStorage;
    if (!api || typeof api.read !== 'function') return '';
    const s = api.read(String(pid), String(vid));
    return typeof s === 'string' ? s.trim() : '';
  } catch {
    return '';
  }
}

/**
 * @param {Element} node
 * @returns {{ productId: string, variantId: string }}
 */
function resolveIds(node) {
  const productId = node.getAttribute('data-dress-name-product-id') || '';
  const source = node.getAttribute('data-dress-name-variant-source') || 'fixed';
  const fixed = node.getAttribute('data-dress-name-variant-id') || '';

  if (source === 'card') {
    const card = node.closest('product-card');
    const checked = card?.querySelector('input[type="radio"][data-variant-id]:checked');
    const vid = checked?.getAttribute('data-variant-id')?.trim();
    if (vid) return { productId, variantId: vid };
    return { productId, variantId: fixed };
  }

  if (source === 'pdp') {
    const form = document.querySelector(`product-form-component[data-product-id="${productId}"]`);
    const inp =
      form?.querySelector('input[name="id"]') ||
      document.querySelector('.product-details input[name="id"]');
    const vid = inp instanceof HTMLInputElement ? inp.value.trim() : '';
    if (vid) return { productId, variantId: vid };
    return { productId, variantId: fixed };
  }

  return { productId, variantId: fixed };
}

/** @param {Element} el */
function applyDressNameToNode(el) {
  const catalog = el.getAttribute('data-catalog-title') || '';
  const mode = el.getAttribute('data-dress-name-for') || 'text';
  const targetSel = el.getAttribute('data-dress-name-target');
  const target = targetSel ? el.querySelector(targetSel) : el;
  if (!target) return;

  const { productId, variantId } = resolveIds(el);
  if (!productId || !variantId) return;

  const source = el.getAttribute('data-dress-name-variant-source') || 'fixed';
  const stored = source === 'card' ? '' : readDressName(productId, variantId);
  const display = stored || catalog;

  if (mode === 'aria-label') {
    target.setAttribute('aria-label', display);
  } else {
    target.textContent = display;
  }

  el.setAttribute('data-dress-name-ready', '');
}

/**
 * @param {ParentNode} [root]
 */
export function syncDressNameDisplays(root = document) {
  if (!root?.querySelectorAll) return;
  root.querySelectorAll('[data-dress-name-display]').forEach((el) => {
    if (el instanceof Element) applyDressNameToNode(el);
  });
}

function dispatchSync() {
  if (typeof window.__beDressNameDisplaySync === 'function') {
    window.__beDressNameDisplaySync();
    return;
  }
  syncDressNameDisplays(document);
}

if (typeof window !== 'undefined' && typeof window.__beDressNameDisplaySync !== 'function') {
  window.__beDressNameDisplaySync = dispatchSync;
}

document.addEventListener('be:dress-name-changed', dispatchSync);
window.addEventListener('storage', (e) => {
  if (typeof e.key === 'string' && e.key.startsWith('be_dress_name:')) dispatchSync();
});

document.addEventListener(ThemeEvents.cartUpdate, () => {
  requestAnimationFrame(dispatchSync);
});

document.addEventListener('shopify:section:load', dispatchSync);

/** @type {ReturnType<typeof setTimeout> | undefined} */
let variantUpdateDressTimer;
document.addEventListener(
  ThemeEvents.variantUpdate,
  () => {
    clearTimeout(variantUpdateDressTimer);
    variantUpdateDressTimer = setTimeout(dispatchSync, 80);
  },
  true
);
